import React from 'react';
import PropTypes from 'prop-types';
import { gettext, translationPropType } from '@eb/i18n';
import classNames from 'classnames';
import isFunction from 'lodash/isFunction';

import { withMainControls } from '@eb/eds-structure';
import { Button } from '@eb/eds-button';
import {
    TYPE_BUTTON,
    TYPE_SUBMIT,
    STYLE_FILL,
    SIZE_RESPONSIVE,
    SIZE_BLOCK,
} from '@eb/eds-button';
import { ProgressIndicator } from '@eb/eds-progress-indicator';

import './EventPageActionBar.scss';

const ProgressStepper = ({
    isStepperRequired,
    step,
    totalSteps,
    onProgressStepperGetText,
}) => {
    if (!isStepperRequired) {
        return null;
    }
    const progress = (step / totalSteps) * 100;
    const stepperClasses = classNames(
        'eds-g-cell',
        'eds-g-cell-1-1',
        'eds-g-cell-sw-1-4',
        'eds-l-pad-bot-6',
        'eds-l-lw-pad-bot-0',
        'eds-l-lg-pad-bot-0',
        'eds-l-ln-pad-bot-0',
        'eds-l-mw-pad-bot-0',
        'eds-l-md-pad-bot-0',
        'eds-l-mn-pad-bot-0',
        'eds-l-sw-pad-bot-0',
        'eds-text--left',
    );
    let labelText;
    let labelClass = 'eds-l-pad-bot-6';

    if (isFunction(onProgressStepperGetText)) {
        labelText = onProgressStepperGetText({ step, totalSteps });
        labelClass = 'eds-l-pad-bot-1';
    }

    return (
        <div className={stepperClasses} data-spec="action-bar-stepper">
            <div className={labelClass} data-spec="action-bar-stepper-label">
                {labelText}
            </div>
            <ProgressIndicator
                shape="linear"
                type="determinate"
                progress={progress}
                style="gradient"
            />
        </div>
    );
};

export const EventActionBar = ({
    isLoading,
    onDiscard,
    onSave,
    disabled,
    visible,
    submitText,
    disableSubmit,
    discardText,
    hideDiscardButton,
    hideSubmitButton,
    isStepperRequired,
    step,
    totalSteps,
    onProgressStepperGetText,
    prependContent,
    showResponsiveMobileButtonSize,
    className,
}) => {
    let discardButton;
    let discardButtonBlock;
    let submitButton;
    let mobileSubmitButton;
    let mobileSubmitButtonSize = SIZE_BLOCK;
    const stepperWithButtons = classNames({
        'eds-g-cell eds-g-cell-1-1 eds-g-cell-sw-3-4': isStepperRequired,
        [className]: className !== undefined,
    });
    const mobileSubmitContainerClassnames = classNames(
        'eds-l-mar-bot-2 eds-show-down-sm',
        {
            'eds-g-cell-1-2': prependContent,
        },
    );

    if (prependContent || showResponsiveMobileButtonSize) {
        mobileSubmitButtonSize = SIZE_RESPONSIVE;
    }

    if (!hideDiscardButton) {
        discardButton = (
            <Button
                type={TYPE_BUTTON}
                disabled={disabled}
                onClick={onDiscard}
                __containerClassName="eds-l-mar-right-4 eds-show-up-sw"
                data-spec="event-page-action-discard"
                data-automation="coyote-action-discard"
            >
                {discardText}
            </Button>
        );
        // Second discard and submit buttons have been added that are only shown in mobile sizes to prevent odd stacking with the current buttons.
        // Due to the nature of having different styling at mobile sizes, we created a separate almost identical button
        // because of the limitations of computing screen size in javascript and being able to use the approved EDS classes.
        discardButtonBlock = (
            <Button
                size={SIZE_BLOCK}
                type={TYPE_BUTTON}
                disabled={disabled}
                onClick={onDiscard}
                __containerClassName="eds-show-down-sm"
                data-spec="event-page-action-discard-mobile"
                data-automation="coyote-action-discard-mobile"
            >
                {discardText}
            </Button>
        );
    }

    if (!hideSubmitButton) {
        submitButton = (
            <Button
                type={TYPE_SUBMIT}
                style={STYLE_FILL}
                disabled={disableSubmit || disabled}
                onClick={onSave}
                isLoading={isLoading}
                __containerClassName="eds-show-up-sw"
                data-spec="event-page-action-save"
                data-automation="coyote-action-save"
            >
                {submitText}
            </Button>
        );

        mobileSubmitButton = (
            <Button
                size={mobileSubmitButtonSize}
                type={TYPE_SUBMIT}
                style={STYLE_FILL}
                disabled={disableSubmit || disabled}
                onClick={onSave}
                isLoading={isLoading}
                __containerClassName={mobileSubmitContainerClassnames}
                data-spec="event-page-action-save-mobile"
                data-automation="coyote-action-save-mobile"
            >
                {submitText}
            </Button>
        );
    }

    return (
        <div
            className="eds-g-grid eds-l-pad-vert-4 coyote-action-bar"
            data-automation="coyote-action-bar"
            data-automation-visible={visible}
            data-spec="event-action-bar"
        >
            <div className="eds-text--right eds-g-cell eds-g-cell-12-12 eds-g-cell-sw-10-12 eds-g-cell-lw-10-12 eds-g-cell-md-9-12 event-page-action-bar">
                <ProgressStepper
                    isStepperRequired={isStepperRequired}
                    step={step}
                    totalSteps={totalSteps}
                    onProgressStepperGetText={onProgressStepperGetText}
                />
                <div className={stepperWithButtons}>
                    {prependContent}
                    {discardButton}
                    {submitButton}
                    {mobileSubmitButton}
                    {discardButtonBlock}
                </div>
            </div>
        </div>
    );
};

/**
 * Event Page specific implementation of the Action Bar
 * NOTE: This is more or less a placeholder to be replaced by an EDS component later
 * @extends React.Component
 * @example
 * // <EventPageActionBar
 * //     isLoading={false}
 * //     onSave={saveFunctionHere}
 * //     onDiscard={discardFunctionHere}
 * // />
 */
class EventPageActionBar extends React.Component {
    static propTypes = {
        visible: PropTypes.bool,
        isLoading: PropTypes.bool,
        onSave: PropTypes.func,
        onDiscard: PropTypes.func,
        showMainBottomBar: PropTypes.func,
        closeMainBottomBar: PropTypes.func,
        discardText: translationPropType,
        submitText: translationPropType,
        disableSubmit: PropTypes.bool,
        hideDiscardButton: PropTypes.bool,
        hideSubmitButton: PropTypes.bool,
        isStepperRequired: PropTypes.bool,
        step: PropTypes.number,
        totalSteps: PropTypes.number,
        onProgressStepperGetText: PropTypes.func,
        prependContent: PropTypes.element,
        showResponsiveMobileButtonSize: PropTypes.bool,
        className: PropTypes.string,
    };

    static defaultProps = {
        discardText: gettext('Discard'),
        submitText: gettext('Save'),
        disableSubmit: false,
        hideDiscardButton: false,
        hideSubmitButton: false,
    };

    componentDidMount() {
        const { visible } = this.props;

        this._displayMainBottomBar(visible);
    }

    componentDidUpdate() {
        const { visible } = this.props;

        this._displayMainBottomBar(visible);
    }

    componentWillUnmount() {
        const { closeMainBottomBar } = this.props;

        closeMainBottomBar();
    }

    _displayMainBottomBar() {
        const {
            visible,
            isLoading,
            onSave,
            onDiscard,
            showMainBottomBar,
            closeMainBottomBar,
            discardText,
            submitText,
            disableSubmit,
            hideDiscardButton,
            isStepperRequired,
            step,
            totalSteps,
            onProgressStepperGetText,
            hideSubmitButton,
            prependContent,
            showResponsiveMobileButtonSize,
            className,
        } = this.props;

        if (visible) {
            showMainBottomBar({
                barContent: (
                    <EventActionBar
                        isLoading={isLoading}
                        onDiscard={onDiscard}
                        onSave={onSave}
                        disabled={isLoading}
                        visible={true}
                        discardText={discardText}
                        submitText={submitText}
                        disableSubmit={disableSubmit}
                        hideDiscardButton={hideDiscardButton}
                        hideSubmitButton={hideSubmitButton}
                        isStepperRequired={isStepperRequired}
                        step={step}
                        totalSteps={totalSteps}
                        onProgressStepperGetText={onProgressStepperGetText}
                        prependContent={prependContent}
                        showResponsiveMobileButtonSize={
                            showResponsiveMobileButtonSize
                        }
                        className={className}
                    />
                ),
            });
        } else {
            // set the contents of the bar to disabled buttons with no actions.  this way,
            // they're not focusable and in case they're accidentally visible (or otherwise
            // still accessible), make sure they do nothing before you hide them.
            showMainBottomBar({
                barContent: (
                    <EventActionBar
                        disabled={true}
                        visible={false}
                        discardText={discardText}
                        submitText={submitText}
                        hideDiscardButton={hideDiscardButton}
                        isStepperRequired={isStepperRequired}
                        step={step}
                        totalSteps={totalSteps}
                        onProgressStepperGetText={onProgressStepperGetText}
                        prependContent={prependContent}
                        showResponsiveMobileButtonSize={
                            showResponsiveMobileButtonSize
                        }
                        className={className}
                    />
                ),
            });
            closeMainBottomBar();
        }
    }

    render() {
        return null;
    }
}

export default withMainControls(EventPageActionBar);
